import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Tabs Component
class TabsCustom extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');
    this.activeTab = null;
    this.activePanel = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Set up event listeners for tabs
    this.tabs.forEach(tab => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
      tab.addEventListener('keydown', this.handleKeydown.bind(this));
    });

    // Set initial active tab
    this.setActiveTab(this.tabs[0]);
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    this.setActiveTab(tab);
  }

  handleKeydown(event) {
    const tab = event.currentTarget;
    const currentIndex = Array.from(this.tabs).indexOf(tab);
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.setActiveTab(tab);
        return;
    }

    if (newIndex !== currentIndex) {
      this.tabs[newIndex].focus();
    }
  }

  setActiveTab(tab) {
    if (!tab) return;

    // Remove active state from all tabs and panels
    this.tabs.forEach(t => {
      t.classList.remove('tabs-block__tab--active');
      t.setAttribute('aria-selected', 'false');
    });

    this.panels.forEach(p => {
      p.classList.remove('tabs-block__panel--active');
    });

    // Set active state on selected tab
    tab.classList.add('tabs-block__tab--active');
    tab.setAttribute('aria-selected', 'true');

    // Find and activate corresponding panel
    const panelId = tab.getAttribute('data-tab-target');
    const panel = this.querySelector(`#${panelId}`);
    
    if (panel) {
      panel.classList.add('tabs-block__panel--active');
      this.activePanel = panel;
    }

    this.activeTab = tab;

    // Dispatch custom event for other components
    this.dispatchEvent(new CustomEvent('tab-change', {
      detail: {
        tab: tab,
        panel: panel,
        tabIndex: Array.from(this.tabs).indexOf(tab)
      }
    }));
  }

  // Public method to programmatically switch tabs
  switchToTab(index) {
    if (index >= 0 && index < this.tabs.length) {
      this.setActiveTab(this.tabs[index]);
    }
  }

  // Public method to get current active tab index
  getActiveTabIndex() {
    return Array.from(this.tabs).indexOf(this.activeTab);
  }
}

// Register the custom element
customElements.define('tabs-custom', TabsCustom);

// Export for potential use in other modules
export { TabsCustom };
